import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { motion } from "framer-motion";
import { getArticle, articles } from "../journal";
import { EASE, useLenis, waLink, fbLead } from "../components/site/primitives";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";

const formatDate = (isoDate, displayFallback) => {
  if (displayFallback) return displayFallback;
  try {
    return new Date(isoDate).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return isoDate;
  }
};

export default function Article() {
  useLenis();
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) return <Navigate to="/journal" replace />;

  const url = `https://www.hitakari.com/journal/${article.slug}`;
  const otherArticles = articles.filter((a) => a.slug !== article.slug).slice(0, 2);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: article.title,
    description: article.metaDescription || article.standfirst,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "en",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    author: {
      "@id": "https://www.hitakari.com/#founder",
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@id": "https://www.hitakari.com/#organization",
      "@type": "Organization",
      name: "Hitakari House",
      logo: {
        "@type": "ImageObject",
        url: "https://www.hitakari.com/hitakari-logo.png",
      },
    },
    image: "https://www.hitakari.com/og-image.jpg",
  };

  return (
    <>
      <Helmet>
        <title>{`${article.title} | Hitakari House`}</title>
        <meta name="description" content={article.metaDescription || article.standfirst} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={`${article.title} | Hitakari House`} />
        <meta property="og:description" content={article.metaDescription || article.standfirst} />
        <meta property="og:image" content="https://www.hitakari.com/og-image.jpg" />
        <meta property="article:author" content={article.author} />
        <meta property="article:published_time" content={article.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} | Hitakari House`} />
        <meta name="twitter:description" content={article.metaDescription || article.standfirst} />
        <meta name="twitter:image" content="https://www.hitakari.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div style={{ background: "var(--ivory)", color: "var(--forest)", minHeight: "100vh" }} data-testid={`article-page-${article.slug}`}>
        <Nav />

        <main className="pt-36 md:pt-44 pb-24 px-6 md:px-10">
          <article className="mx-auto max-w-[720px]">
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <Link
                to="/journal"
                className="font-label uppercase text-[0.7rem] tracking-[0.28em] font-light link-underline-slow"
                style={{ color: "var(--brass)" }}
                data-testid="article-back-link"
              >
                &larr; &nbsp; The Journal
              </Link>

              <h1 className="font-heading font-light leading-[1.08] tracking-[-0.01em] mt-8 text-[2.2rem] sm:text-[3rem] lg:text-[3.6rem]">
                {article.title}
              </h1>

              <p className="font-body italic text-xl md:text-2xl mt-6 leading-snug" style={{ color: "var(--olive)" }}>
                {article.standfirst}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-label uppercase text-[0.7rem] tracking-[0.24em] font-light" style={{ color: "var(--forest)", opacity: 0.65 }}>
                <span>{article.author}</span>
                <span aria-hidden>&middot;</span>
                <span>{formatDate(article.date, article.dateDisplay)}</span>
                {article.readingTime && (
                  <>
                    <span aria-hidden>&middot;</span>
                    <span>{article.readingTime}</span>
                  </>
                )}
              </div>
            </motion.header>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.1, delay: 0.15, ease: EASE }}
              className="prose-hitakari mt-16"
            >
              <ReactMarkdown>{article.body}</ReactMarkdown>
            </motion.div>

            <div className="mt-20 pt-10" style={{ borderTop: "1px solid rgba(23,64,42,0.2)" }}>
              <p className="font-body text-xl leading-relaxed" style={{ color: "var(--olive)" }}>
                Hitakari House is a private, invitation-only house of hospitality across India. If any of this resonates, and someone in the circle has already spoken your name, we'd be honoured to hear from you.
              </p>
              <a
                href={waLink("\nI read your journal.")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fbLead("whatsapp")}
                className="inline-block mt-8 font-label text-[0.78rem] tracking-[0.24em] uppercase px-9 py-4 font-light"
                style={{ backgroundColor: "var(--forest)", color: "var(--ivory)", transition: "background-color 200ms ease, color 200ms ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "var(--forest)"; e.currentTarget.style.boxShadow = "inset 0 0 0 1px var(--forest)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--forest)"; e.currentTarget.style.color = "var(--ivory)"; e.currentTarget.style.boxShadow = "none"; }}
                data-testid="article-whatsapp-btn"
              >
                Engage with Us
              </a>
            </div>
          </article>

          {otherArticles.length > 0 && (
            <div className="mx-auto max-w-[900px] mt-28">
              <span className="eyebrow" style={{ color: "var(--brass)" }}>Also in the Journal</span>
              <div className="mt-8 grid md:grid-cols-2 gap-8">
                {otherArticles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/journal/${a.slug}`}
                    className="group block py-6"
                    style={{ borderTop: "1px solid rgba(23,64,42,0.2)" }}
                  >
                    <p className="font-label uppercase text-[0.7rem] tracking-[0.24em] font-light" style={{ color: "var(--brass)" }}>
                      {formatDate(a.date, a.dateDisplay)}
                    </p>
                    <h3 className="font-heading font-light text-[1.5rem] md:text-[1.75rem] mt-3 leading-tight link-underline-slow">
                      {a.title}
                    </h3>
                    <p className="font-body italic text-lg mt-2" style={{ color: "var(--olive)" }}>
                      {a.standfirst}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}
